import {SendOutlined} from "@ant-design/icons";
import {message, Alert, Spin, Input, Tooltip, Avatar} from "antd";
import moment from "moment";
import {useState, useRef} from "react";
import {useSelector} from "react-redux";
import {useMessageContext} from "../../../../context/message-context";
import {
  useGetCustomerMessageHistory,
  useSendMessageCustomer,
  TSendMessageCustomerPayload,
} from "../../../../react-query-hooks/messages";
import {RootState} from "../../../../redux/store";
import {StyledNoUpcomingCallBox} from "../../../calls/upcoming-calls/upcoming-calls.style";
import {FullPageLoader} from "../../../full-page-loader";
import {LoadingSpinner} from "../../../icons/icons";
import {
  StyledChatBox,
  StyledNoChatBox,
  StyleSingleChatBox,
  StyledSentMessageBox,
  StyledReceivedMessageBox,
} from "../chat-list.style";
import {capitalizeFirstLetter} from "../../../../services/helpers";

const CustomerChatList = () => {
  const {state} = useMessageContext();
  const [typedMessage, setTypedMessage] = useState("");
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const contactToQuery = state.customerContacts.find(
    (contact) =>
      contact.mentorName + contact.mentorEmailAddress ===
      (((state.customerActiveMessage?.mentorName as string) +
        state.customerActiveMessage?.mentorEmailAddress) as string)
  );
  const getCustomerMessageHistoryQuery = useGetCustomerMessageHistory(
    contactToQuery?.id,
    contactToQuery?.userId
  );
  const sendMessageCustomerQueryMutation = useSendMessageCustomer();

  const scrollRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: any) => {
    setTypedMessage(e.target.value);
  };

  const payload: TSendMessageCustomerPayload = {
    message: typedMessage,
    userId: state.customerActiveMessage?.userId as string,
  };

  const handleEnter = (e: any) => {
    if (typedMessage) {
      sendMessageCustomerQueryMutation.mutate(payload, {
        onSuccess: (data) => {
          message.success("Message delivered successfully!");
          scrollRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          // scrollRef.current?.scrollTo(0, 0);
        },
      });
      setTypedMessage("");
    } else {
      message.warning("No text!");
    }
  };
  const handleEnterKey = (e: any) => {
    e.preventDefault();
    if (e.keyCode === 13 && typedMessage) {
      sendMessageCustomerQueryMutation.mutate(payload, {
        onSuccess: (data) => {
          message.success("Message delivered successfully!");
          scrollRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        },
      });
      setTypedMessage("");
    }
  };
  return (
    <StyledChatBox>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          height: "100%",
        }}
      >
        {/* <StyledCustomerBox> */}
        {!state.customerActiveMessage ? (
          <StyledNoChatBox>
            <StyledNoUpcomingCallBox>
              <p className="info-text">You don't have a selected message</p>
              <p className="info-text" style={{maxWidth: "50rem"}}>
                Choose one from your existing messages, or start a new one
              </p>
            </StyledNoUpcomingCallBox>
          </StyledNoChatBox>
        ) : (
          <>
            {getCustomerMessageHistoryQuery.isLoading ? (
              <FullPageLoader />
            ) : getCustomerMessageHistoryQuery.isError &&
              getCustomerMessageHistoryQuery.error.message !==
                "Start by sending a message!" ? (
              <Alert
                message="Error"
                description={getCustomerMessageHistoryQuery.error?.message}
                type="error"
                showIcon
              />
            ) : (
              <>
                <Spin
                  spinning={getCustomerMessageHistoryQuery.isFetching}
                  // tip="Updating..."
                  indicator={LoadingSpinner}
                >
                  <div
                    style={{
                      height: "60.8rem",
                      overflowY: "scroll",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "sticky",
                        top: 0,
                        left: 0,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #f5edff",
                        padding: "1rem 2.4rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        <Avatar
                          size="large"
                          style={{color: "#43375D", backgroundColor: "#EAE6F3"}}
                        >
                          {state.customerActiveMessage.mentorName
                            .split(" ")[1]
                            .charAt(0)
                            .toUpperCase()}
                          {state.customerActiveMessage.mentorName
                            .split(" ")[0]
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                        <div>
                          <h4>
                            {capitalizeFirstLetter(
                              state.customerActiveMessage.mentorName.split(" ")[1]
                            )}{" "}
                            {capitalizeFirstLetter(
                              state.customerActiveMessage.mentorName.split(" ")[0]
                            )}
                          </h4>
                          <p>{state.customerActiveMessage.mentorEmailAddress}</p>
                        </div>
                      </div>
                    </div>
                    <div style={{padding: "2.4rem", paddingTop: "6rem"}}>
                      {getCustomerMessageHistoryQuery.data?.map((msgHistory) =>
                        userInfo?.user?.emailAddress === msgHistory.sender ? (
                          <div key={msgHistory.id} ref={scrollRef}>
                            <StyleSingleChatBox type={"sent"}>
                              <StyledSentMessageBox>
                                <p>{msgHistory.message}</p>
                              </StyledSentMessageBox>
                              <p className="chat-time">
                                {moment(msgHistory.createdAt).fromNow()}
                              </p>
                            </StyleSingleChatBox>
                          </div>
                        ) : (
                          <div key={msgHistory.id} ref={scrollRef}>
                            <StyleSingleChatBox type={"received"}>
                              <StyledReceivedMessageBox>
                                <p>{msgHistory.message}</p>
                              </StyledReceivedMessageBox>
                              <p className="chat-time">
                                {moment(msgHistory.createdAt).fromNow()}
                              </p>
                            </StyleSingleChatBox>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <div style={{padding: "2.4rem"}}>
                    <Input
                      placeholder="Type message"
                      suffix={
                        <Tooltip title="Send">
                          <SendOutlined
                            onClick={handleEnter}
                            style={{color: "#222222", cursor: "pointer"}}
                          />
                        </Tooltip>
                      }
                      value={typedMessage}
                      onChange={handleChange}
                      onKeyUp={handleEnterKey}
                    />
                  </div>
                </Spin>
              </>
            )}
          </>
        )}

        {/* </StyledChatListBox> */}
        {/* {state.customerActiveMessage && (
            <div style={{padding: "2.4rem"}}>
              <Input
                placeholder="Type message"
                suffix={
                  <Tooltip title="Send">
                    <SendOutlined
                      onClick={handleEnter}
                      style={{color: "#222222", cursor: "pointer"}}
                    />
                  </Tooltip>
                }
                value={typedMessage}
                onChange={handleChange}
                onKeyUp={handleEnterKey}
              />
            </div>
          )} */}
      </div>
    </StyledChatBox>
  );
};

export {CustomerChatList};
